"use client";

import { useState } from "react";
import Image from "next/image";
import { IpoCard } from "@/app/components/icm 3.0/shared/IpoCard";
import { ProductCard } from "@/app/components/icm 3.0/shared/ProductCard";
import ChevronDown from "@/app/assets/icons/chevron-down";
import stocksHandshake from "@/app/assets/images/icm-3.0/stocks.gif";
import globalStocksGlobe from "@/app/assets/images/icm-3.0/global-stocks.gif";
import mutualFundsBag from "@/app/assets/images/icm-3.0/mutual-funds.gif";
import digitalGoldBars from "@/app/assets/images/icm-3.0/digital-gold.gif";
import fnoCandlesticks from "@/app/assets/images/icm-3.0/fno.gif";
import fdSafe from "@/app/assets/images/icm-3.0/fd.gif";
import { DirectPaths } from "@/config/routes";
import { cn } from "@/lib/utils";

/**
 * Explore InCred Money product grid
 * Desktop: Figma 5882:65739
 * Mobile:  Figma 6679:10749 / 2541:30557
 */
export function ProductGrid() {
  const [showMore, setShowMore] = useState(false);

  return (
    <div className="flex w-full flex-col items-center gap-6 md:gap-12">
      <header className="flex w-full flex-col items-center gap-1 text-center md:gap-2">
        <p className="w-full text-center text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px]">
          Explore InCred Money
        </p>
        <h2 className="text-center text-2xl font-bold leading-8 tracking-[0] text-black md:text-5xl md:leading-[58px]">
          Grow your money, your way.
        </h2>
      </header>

      {/* Mobile: stacked 361-wide cards, 24px gaps. Desktop: mosaic */}
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-[595px_minmax(0,1fr)] md:gap-5">
        <ProductCard
          badge="BROKING"
          badgeVariant="orange"
          title="Stocks"
          description="Trade stocks with zero brokerage. Fast execution and powerful tools, built for active investors."
          ctaText="Start Trading"
          ctaHref={DirectPaths.BROKING}
          analyticsEvent="Home_Explore_Indian_Stocks"
          newLabel="New!"
          textSize="text-2xl leading-8 md:text-5xl md:leading-[58px]"
          className="h-[286px] w-full bg-[linear-gradient(155deg,#FCEAE0_1.03%,#FFF_83.87%)] md:h-[512px] md:w-[595px] md:bg-[linear-gradient(157.72deg,#FCEAE0_1.03%,#FFF_83.87%)]"
          contentClassName="max-w-[202px] md:max-w-[340px]"
          descriptionClassName="max-w-[271px] leading-5 md:max-w-none md:text-lg md:leading-6"
          illustrationClassName="absolute bottom-1 right-[15px] size-[124px] md:bottom-auto md:left-[calc(50%+142px)] md:right-auto md:top-[calc(50%+113.5px)] md:size-[285px] md:-translate-x-1/2 md:-translate-y-1/2"
        >
          <Image
            src={stocksHandshake}
            alt=""
            width={285}
            height={285}
            className="size-full object-contain"
            unoptimized
            aria-hidden
          />
        </ProductCard>

        <div className="flex flex-col gap-6 md:gap-5">
          <ProductCard
            badge="INVEST INTERNATIONALLY"
            badgeVariant="green"
            title="Global Stocks"
            description="Access global investing and build a portfolio beyond India."
            ctaText="Explore global stocks"
            ctaHref={`${DirectPaths.GLOBAL}${DirectPaths.US_STOCKS}`}
            analyticsEvent="Home_Nav_Bar_US_Stocks"
            textSize="text-2xl leading-8 md:text-[30px] md:leading-9"
            className="h-[246px] w-full bg-[linear-gradient(155deg,#F0FDF4_1.03%,#FFF_83.87%)] md:h-[246px] md:w-[585px] md:bg-[linear-gradient(168.68deg,#F0FDF4_1.03%,#FFF_83.87%)]"
            contentClassName="max-w-[202px] md:max-w-[422px]"
            descriptionClassName="max-w-[236px] leading-5 md:max-w-[320px] md:text-lg md:leading-6"
            illustrationClassName="absolute bottom-1.5 right-[9px] size-[101px] md:bottom-auto md:left-[calc(50%+204px)] md:right-auto md:top-[calc(50%+29px)] md:h-[148px] md:w-[149px] md:-translate-x-1/2 md:-translate-y-1/2"
          >
            <Image
              src={globalStocksGlobe}
              alt=""
              width={149}
              height={148}
              className="size-full object-contain"
              unoptimized
              aria-hidden
            />
          </ProductCard>

          <ProductCard
            badge="WEALTH BUILDING"
            badgeVariant="orange"
            badgeClassName="bg-[#FAFAFA]"
            title="Mutual Funds"
            description="Diversify your portfolio with top funds in equity, debt, and hybrid."
            ctaText="Invest Now"
            ctaHref="#"
            textSize="text-2xl leading-8 md:text-4xl md:leading-[44px]"
            className="h-[246px] w-full bg-[linear-gradient(155deg,#DEE8FB_1.03%,#FEF5EE_83.87%)] md:h-[246px] md:w-[585px] md:bg-[linear-gradient(166.98deg,#DEE8FB_1.03%,#FEF5EE_83.87%)]"
            contentClassName="max-w-[202px] md:max-w-[340px]"
            descriptionClassName="max-w-[236px] leading-5 md:max-w-none md:text-lg md:leading-6"
            illustrationClassName="absolute bottom-0 right-0 h-[200px] w-auto md:bottom-auto md:left-[calc(50%+218px)] md:right-auto md:top-[calc(50%+27.5px)] md:size-[250px] md:-translate-x-1/2 md:-translate-y-1/2"
          >
            <Image
              src={mutualFundsBag}
              alt=""
              width={169}
              height={169}
              className="h-full w-auto object-contain md:size-full"
              unoptimized
              aria-hidden
            />
          </ProductCard>
        </div>
      </div>

      <div
        className={cn(
          "w-full flex-col gap-6 md:gap-5",
          showMore ? "flex" : "hidden md:flex"
        )}
      >
        <div className="flex flex-col gap-6 md:flex-row md:gap-5">
          <IpoCard className="w-full md:h-[564px] md:w-[800px] md:shrink-0" />

          <ProductCard
            badge="DIGITAL GOLD & SILVER"
            badgeVariant="yellow"
            title={
              <>
                Start with
                <br />
                just ₹10.
              </>
            }
            description="Buy, save, and grow your wealth with 99.9% pure digital gold & silver."
            attribution="Powered by MMTC - PAMP"
            ctaText="Start SIP"
            ctaHref={`${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.GOLD}`}
            analyticsEvent="Home_Explore_on_Digital_Gold&_Silver"
            textSize="text-2xl leading-8 md:text-4xl md:leading-[44px]"
            className="min-h-[280px] w-full bg-[linear-gradient(145deg,#FEFCE8_1.03%,#FFF_83.87%)] md:h-[564px] md:w-[380px] md:shrink-0 md:bg-[linear-gradient(144.76deg,#FEFCE8_1.03%,#FFF_83.87%)]"
            contentClassName="max-w-[210px] gap-6 md:max-w-[235px]"
            descriptionClassName="leading-6 md:text-lg"
            illustrationClassName="absolute bottom-4 right-4 size-[120px] md:bottom-auto md:left-[calc(50%+72px)] md:right-auto md:top-[calc(50%+187px)] md:size-[236px] md:-translate-x-1/2 md:-translate-y-1/2"
          >
            <Image
              src={digitalGoldBars}
              alt=""
              width={236}
              height={236}
              className="size-full object-contain"
              unoptimized
              aria-hidden
            />
          </ProductCard>
        </div>

        <div className="flex flex-col gap-6 md:flex-row md:gap-5">
          <ProductCard
            badge="FUTURES & OPTIONS"
            badgeVariant="orange"
            badgeClassName="border-[#FDE7D7] bg-[#FAFAFA] text-[#E13C15]"
            title="Trade FnO with precision."
            description="Access FnO trading with advanced tools for speed and strategy."
            ctaText="Trade FnO"
            ctaHref={DirectPaths.BROKING}
            analyticsEvent="Home_Nav_Bar_Broking"
            textSize="text-2xl leading-8 md:text-[30px] md:leading-9"
            className="min-h-[200px] w-full bg-[linear-gradient(169deg,#FEF2F2_1.03%,#FFF_83.87%)] md:h-[239px] md:min-h-[239px] md:w-[590px] md:shrink-0"
            contentClassName="max-w-[210px] md:max-w-[374px]"
            descriptionClassName="max-w-[287px] leading-[22px]"
            illustrationClassName="absolute bottom-3 right-3 w-[88px] md:bottom-auto md:left-[446px] md:right-auto md:top-[65px] md:h-[170px] md:w-[130px]"
          >
            <Image
              src={fnoCandlesticks}
              alt=""
              width={121}
              height={158}
              className="h-auto w-full object-contain"
              unoptimized
              aria-hidden
            />
          </ProductCard>

          <ProductCard
            badge="FIXED DEPOSITS"
            badgeVariant="blue"
            title="Secure high-yield bank FDs"
            description={
              <>
                Compare 200+ Fds from AAA-rated banks & NBFCs.
                <br className="hidden md:block" />
                Find the right balance of safety, flexibility, and returns.
              </>
            }
            ctaText="Compare Rates"
            ctaHref={DirectPaths.FD_POSTFIX_PATH}
            analyticsEvent="Home_Explore_Fixed_Deposit"
            ctaClassName="text-cta-ghost-text"
            textSize="text-2xl leading-8 md:text-[30px] md:leading-9"
            className="h-[299px] w-full bg-[linear-gradient(158.48deg,#DEE8FB_1.03%,#FFF_83.87%)] md:h-[237px] md:min-h-[237px] md:w-[590px] md:shrink-0 md:bg-[linear-gradient(169.17deg,#DEE8FB_1.03%,#FFF_83.87%)]"
            contentClassName="max-w-[260px] md:max-w-[403px]"
            descriptionClassName="max-w-[260px] text-sm leading-5 md:max-w-none md:text-base md:leading-[22px]"
            illustrationClassName="absolute left-[calc(50%+127px)] top-[calc(50%+97px)] size-[107px] -translate-x-1/2 -translate-y-1/2 md:left-[calc(50%+221.5px)] md:top-[calc(50%+43px)] md:size-[167px]"
          >
            <Image
              src={fdSafe}
              alt=""
              width={167}
              height={167}
              className="size-full object-cover"
              unoptimized
              aria-hidden
            />
          </ProductCard>
        </div>
      </div>

      <div className="flex justify-center md:hidden">
        <button
          type="button"
          aria-expanded={showMore}
          onClick={() => setShowMore((open) => !open)}
          className="inline-flex h-[26px] items-center gap-1 text-sm font-medium leading-5 text-ink-link underline"
        >
          {showMore ? "View Less" : "View More"}
          <ChevronDown
            className={cn(
              "size-4 transition-transform",
              showMore && "rotate-180"
            )}
          />
        </button>
      </div>
    </div>
  );
}
